<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'

import TrackingPixel from '../../components/tracking/TrackingPixel.vue'
import { useTeaser } from '../../composables/use-teaser'
import { useImgSrcSet } from '../../composables/use-img-src-set'
import { useLazyLoadingUtils } from '../../composables/use-lazy-loading-utils'
import TeaserPicture from './TeaserPicture.vue'
import TeaserTopBadges from './TeaserTopBadges.vue'
import TeaserMediaIcons from './TeaserMediaIcons.vue'
import TeaserSliderControls from './TeaserSliderControls.vue'

import type { TeaserProps } from '../../composables/use-teaser'

const props = withDefaults(defineProps<TeaserProps>(), {
  includeDepth: 0,
  listOrderPosition: 0,
  isInlineTeaser: false,
  dataSliderInitialSlide: false,
  prioritizeImage: false
})

const {
  fallbackPlaceholderSource,
  showInlineTeaserSliderStyle,
  setBrandBadgeAlt,
  setLinkTarget,
  showDownloadIcon,
  showPlayIcon,
  showGalleryIcon,
  showAdBadge,
  showOwnerBrand,
  imageIsAiGenerated,
  setStyleClasses,
  headline,
  imageAltText,
  copytext,
  imageUrl,
  imageFetchPriority,
  headlineTagType,
  shouldAddDataNoSnippet,
  cdnImageUrl,
  getImageOverlayStyleClass,
  imgSrcSetArray,
  brandBadgeSrc,
  addTeaserImagePreloadLinksToHead
} = useTeaser(props)

const { getImageAltText } = useImgSrcSet()
const { getLoadingValue } = useLazyLoadingUtils(props)

useHead(computed(() => addTeaserImagePreloadLinksToHead()))
</script>

<template>
  <section :class="setStyleClasses">
    <div v-if="composedTeaser.externalCssClass"
         :class="composedTeaser.externalCssClass" />
    <a v-else
       :href="composedTeaser.url"
       :rel="composedTeaser.rel"
       :target="setLinkTarget"
       data-tc="teaser">
      <figure v-if="imageUrl"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <TeaserPicture :src-set-array="imgSrcSetArray('webp')"
                       :fallback-placeholder-source="fallbackPlaceholderSource"
                       :loading="getLoadingValue"
                       :fetch-priority="imageFetchPriority"
                       :alt-text="getImageAltText(composedTeaser, imageAltText)" />
        <TeaserTopBadges :show-owner-brand="showOwnerBrand"
                         :show-ad-badge="showAdBadge"
                         :image-is-ai-generated="imageIsAiGenerated"
                         :is-affiliate="composedTeaser?.affiliate"
                         :brand-badge-alt="setBrandBadgeAlt"
                         :brand-badge-src="brandBadgeSrc" />
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-image">
          <TeaserMediaIcons :show-play-icon="showPlayIcon"
                            :show-download-icon="showDownloadIcon"
                            :show-gallery-icon="showGalleryIcon" />
        </div>

        <template v-if="showInlineTeaserSliderStyle">
          <TeaserSliderControls :num-slides="composedTeaser.numSlides"
                                :slider-contents="composedTeaser.sliderContents" />
        </template>
      </figure>
      <figure v-else-if="metaData?.isSliderPreview"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <img v-if="metaData.isLoading"
             :src="metaData.imgUrlLoading">
        <img v-else-if="metaData.error"
             :src="metaData.imgUrlError">
        <img v-else-if="metaData.errorNotEnoughContent"
             :src="metaData.imgUrlErrorNotEnoughContent">
        <img v-else
             :src="cdnImageUrl('/images/default-placeholder.jpg')">
      </figure>

      <div class="bx-teaser__content"
           data-tc="teaser-content">
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-content">
          <TeaserMediaIcons :show-play-icon="showPlayIcon"
                            :show-download-icon="showDownloadIcon"
                            :show-gallery-icon="showGalleryIcon"
                            variant="content" />
        </div>
        <div class="bx-badges bx-badges--top bx-badges--over-content">
          <span v-if="showAdBadge"
                class="bx-badge bx-badge--ad"
                :class="{ 'bx-badge--affiliate-link': composedTeaser?.affiliate }" />
        </div>

        <div v-if="composedTeaser.roofline"
             class="bx-teaser__roofline"
             :data-nosnippet="shouldAddDataNoSnippet"
             data-tc="teaser-roofline">
          {{ composedTeaser.roofline }}
        </div>

        <component :is="headlineTagType"
                   v-if="headline"
                   :data-nosnippet="shouldAddDataNoSnippet"
                   class="bx-teaser__headline"
                   data-tc="teaser-headline">
          {{ headline }}
        </component>

        <p v-if="copytext"
           :data-nosnippet="shouldAddDataNoSnippet"
           class="bx-teaser__copy">
          {{ copytext }}
        </p>

        <div v-if="showPlayIcon"
             class="bx-badges bx-badges--below-teaser-content">
          <svg class="bx-badge bx-badge--video icon icon-play">
            <use href="#play" />
          </svg>
        </div>
      </div>
      <TrackingPixel v-if="composedTeaser.trackingPixel"
                     :tracking-pixel="composedTeaser.trackingPixel" />
    </a>
  </section>
</template>
